import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import UserService from '../../services/userService';
import { useSupabaseAuthClient } from '../../hooks/useSupabaseAuthClient';
import AuthHeader from '../AuthHeader';
import Footer from '../Footer';

function NewPassword() {
  const [password, setPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [validResetSession, setValidResetSession] = useState(false);
  const [loading, setLoading] = useState(true);

  const params = useParams();
  const navigate = useNavigate();
  const client = useSupabaseAuthClient();

  useEffect(() => {
    const handlePasswordReset = async () => {
      try {
        console.log('Current URL:', window.location.href);
        console.log('URL Hash:', window.location.hash);
        
        // First, check if there are auth fragments in the URL
        const hash = window.location.hash;
        
        if (hash && hash.includes('type=recovery')) {
          console.log('Recovery URL detected, handling auth callback...');
          
          // Handle the auth callback from the URL fragments
          const { data, error } = await client.auth.getSession();
          
          if (error) {
            console.error('Auth callback error:', error);
            setError('There was a problem with your reset link. Please request a new password reset.');
            setLoading(false);
            return;
          }

          if (data?.session) {
            console.log('Session established from URL:', data.session);
            setValidResetSession(true);
            setLoading(false);
            return;
          }
          
          // Wait a bit more and try again
          await new Promise(resolve => setTimeout(resolve, 2000));
          const { data: retryData } = await client.auth.getSession();
          if (retryData?.session) {
            console.log('Session established after retry:', retryData.session);
            setValidResetSession(true);
            setLoading(false);
            return;
          }
        }
        
        // Check for existing session (in case user navigated here directly)
        const { data, error } = await client.auth.getSession();

        if (error) {
          console.error('Session error:', error);
          setError('There was a problem accessing your reset session. Please request a new password reset.');
          setLoading(false);
          return;
        }

        // Check if we have a valid session
        if (data?.session) {
          console.log('Existing valid session found:', data.session);
          setValidResetSession(true);
        } else {
          console.log('No valid session found');
          setError('No valid reset session found. Please click the reset link in your email.');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error setting up password reset session:', error);
        setError('There was a problem processing your reset link. Please request a new password reset.');
        setLoading(false);
      }
    };

    handlePasswordReset();

    // Also listen for auth state changes
    const { data: { subscription } } = client.auth.onAuthStateChange((event, session) => {
      console.log('Auth state change:', event, session);
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setValidResetSession(true);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handlePasswordChange = (event) => {
    event.preventDefault();
    if (event.target.name === 'password') {
      setPassword(event.target.value);
    } else if (event.target.name === 'confirmedPassword') {
      setConfirmedPassword(event.target.value);
    }
  };

  const handleSaveClick = async (event) => {
    event.preventDefault();
    
    if (!password || !confirmedPassword) {
      setError('Both password fields are required.');
      return;
    }
    
    if (password !== confirmedPassword) {
      setError('Passwords must match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setSending(true);
    setError('');

    try {
      // Check if we have a valid session
      const { data: sessionData } = await client.auth.getSession();

      if (!sessionData?.session) {
        throw new Error('No active session found. Please use a valid password reset link.');
      }

      // For password reset, use Supabase directly instead of edge function
      // This ensures we're using the recovery session properly
      const { data, error } = await client.auth.updateUser({
        password: password
      });

      if (error) throw error;

      setSending(false);
      setSent(true);
      
      // Sign out the user so they need to log in with new password
      setTimeout(async () => {
        const { error } = await client.auth.signOut();
        if (error) {
          console.error('Sign out error:', error);
        }
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Password update error:', error);
      setSending(false);
      setError(error.message || 'Failed to update password. Please try again.');
    }
  };

  const renderError = () => {
    if (error) {
      return (
        <div className="alert mt-3 mb-3 alert-danger" role="alert">{error}</div>
      );
    }

    if (sent) {
      return (
        <>
          <div className="alert mt-3 mb-3 alert-success" role="alert">
            Success! Your password has been changed! You will be redirected to the login page in a few seconds.
          </div>
          <Link to="/login" className="btn btn-primary">Sign in now</Link>
        </>
      );
    }

    return null;
  };

  return (
    <div className="auth-page-container">
      <div className="auth-content">
        <div className="container">
          <AuthHeader />
          <div className="auth-form-container">
            <form className="form-signin">
              <h1 className="h3 mb-3 font-weight-normal text-center">Set new password</h1>

              <div className="form-box">
                {loading ? (
                  <div className="text-center">
                    <p>Verifying your reset link...</p>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : !validResetSession && !sent ? (
                  <div className="alert alert-danger" role="alert">
                    Invalid or expired password reset link. Please request a new password reset.
                    <div className="mt-3">
                      <a href="/forgotPassword" className="btn btn-primary">Request New Reset Link</a>
                    </div>
                  </div>
                ) : !sent ? (
                  <>
                    <div className="form-group">
                      <label htmlFor="password">New password</label>
                      <input
                        type="password"
                        required=""
                        className="form-control"
                        id="password"
                        name="password"
                        value={password}
                        onChange={handlePasswordChange}
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label htmlFor="confirmedPassword">Confirm new password</label>
                      <input
                        type="password"
                        required=""
                        className="form-control"
                        id="confirmedPassword"
                        name="confirmedPassword"
                        value={confirmedPassword}
                        onChange={handlePasswordChange}
                      />
                    </div>

                    <button 
                      onClick={handleSaveClick} 
                      className="btn btn-bs btn-primary" 
                      type="submit"
                      disabled={sending}
                    >
                      {sending ? 'Processing...' : 'Save'}
                    </button>
                  </>
                ) : null}
                {renderError()}
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default NewPassword;
